/**
 * Servicio de geocodificación para obtener coordenadas a partir de direcciones
 */

// Coordenadas por defecto (Madrid, España)
const DEFAULT_COORDINATES = {
  lat: 40.4168,
  lng: -3.7038
};

/**
 * Geocodificación usando Google Maps API
 * @param {string} address - Dirección completa
 * @returns {Promise<{lat: number, lng: number}>} Coordenadas
 */
const geocodeWithGoogle = (address) => {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      reject(new Error('Google Maps API no está disponible'));
      return;
    }

    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address }, (results, status) => {
      if (status === 'OK' && results && results.length > 0) {
        const location = results[0].geometry.location;
        resolve({
          lat: location.lat(),
          lng: location.lng()
        });
      } else {
        reject(new Error(`Error de geocodificación: ${status}`));
      }
    });
  });
};

/**
 * Geocodificación usando Nominatim (OpenStreetMap) como fallback
 * @param {string} address - Dirección completa
 * @returns {Promise<{lat: number, lng: number}>} Coordenadas
 */
const geocodeWithNominatim = async (address) => {
  try {
    const encodedAddress = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encodedAddress}&limit=1&addressdetails=1`,
      {
        headers: {
          'User-Agent': 'ProKidsApp/1.0'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Error HTTP: ${response.status}`);
    }

    const data = await response.json();

    if (data && data.length > 0) {
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon)
      };
    } else {
      throw new Error('No se encontraron resultados');
    }
  } catch (error) {
    throw new Error(`Error en Nominatim: ${error.message}`);
  }
};

/**
 * Cargar Google Maps API si no está disponible
 * @returns {Promise<boolean>} True si se carga correctamente
 */
const loadGoogleMapsAPI = () => {
  return new Promise((resolve) => {
    // Si ya está cargado, resolver inmediatamente
    if (window.google && window.google.maps) {
      resolve(true);
      return;
    }

    // Si ya hay un script cargándose, esperar a que termine
    if (window.googleMapsLoading) {
      const checkLoaded = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkLoaded);
          resolve(true);
        }
      }, 100);
      return;
    }

    // Marcar que estamos cargando
    window.googleMapsLoading = true;

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    // Si no hay API key, no intentar cargar
    if (!apiKey) {
      console.warn('Google Maps API key no configurada');
      window.googleMapsLoading = false;
      resolve(false);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.googleMapsLoading = false;
      resolve(true);
    };

    script.onerror = () => {
      console.error('Error al cargar Google Maps API');
      window.googleMapsLoading = false;
      resolve(false);
    };

    document.head.appendChild(script);
  });
};

/**
 * Formatear dirección para geocodificación
 * @param {Object} address - Objeto con datos de dirección
 * @returns {string} Dirección formateada
 */
const formatAddress = (address) => {
  const parts = [];

  if (address.street) parts.push(address.street);
  if (address.city) parts.push(address.city);
  if (address.postalCode) parts.push(address.postalCode);
  if (address.country) parts.push(address.country);

  return parts.join(', ');
};

/**
 * Validar coordenadas
 * @param {Object} coordinates - Objeto con lat y lng
 * @returns {boolean} True si las coordenadas son válidas
 */
const validateCoordinates = (coordinates) => {
  if (!coordinates || typeof coordinates !== 'object') {
    return false;
  }

  const { lat, lng } = coordinates;

  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    lat >= -90 && lat <= 90 &&
    lng >= -180 && lng <= 180 &&
    !isNaN(lat) && !isNaN(lng)
  );
};

/**
 * Obtener coordenadas a partir de una dirección
 * @param {Object} address - Objeto con datos de dirección
 * @returns {Promise<{lat: number, lng: number}>} Coordenadas
 */
const getCoordinatesFromAddress = async (address) => {
  console.log('Obteniendo coordenadas para dirección:', address);

  // Validar que tenemos una dirección válida
  if (!address || !address.street || !address.city) {
    console.warn('Dirección incompleta, usando coordenadas por defecto');
    return DEFAULT_COORDINATES;
  }

  const formattedAddress = formatAddress(address);
  console.log('Dirección formateada:', formattedAddress);

  try {
    // Intentar primero con Google Maps API
    console.log('Intentando geocodificación con Google Maps...');
    await loadGoogleMapsAPI();

    if (window.google && window.google.maps) {
      try {
        const coordinates = await geocodeWithGoogle(formattedAddress);
        console.log('Coordenadas obtenidas con Google Maps:', coordinates);

        if (validateCoordinates(coordinates)) {
          return coordinates;
        }
      } catch (googleError) {
        console.warn('Error con Google Maps:', googleError.message);
      }
    }

    // Si Google Maps falla, usar Nominatim como fallback
    console.log('Intentando geocodificación con Nominatim...');
    const coordinates = await geocodeWithNominatim(formattedAddress);
    console.log('Coordenadas obtenidas con Nominatim:', coordinates);

    if (validateCoordinates(coordinates)) {
      return coordinates;
    }

  } catch (error) {
    console.error('Error en geocodificación:', error);
  }

  // Si todo falla, usar coordenadas por defecto
  console.warn('Geocodificación fallida, usando coordenadas por defecto');
  return DEFAULT_COORDINATES;
};

/**
 * Servicio de geocodificación
 */
const geocodingService = {
  getCoordinatesFromAddress,
  validateCoordinates,
  formatAddress,
  DEFAULT_COORDINATES
};

export default geocodingService;
